import React, {forwardRef, useImperativeHandle, useState} from 'react';
import {Button, Modal} from "react-bootstrap";
import Loading from "../../components/loading";
import {Form, Formik} from "formik";
import {userGroupRequestSchema, userUserGroupRoleRequest} from "../../utils/schemas";
import FormGroup from "../../components/form-group";
import HelperService from "../../services/helper-service";
import Section from "../../components/section";
import {useGlobalNotificationMessage} from "../../context/message";
import {useUserGroupService} from "../../services/user-group-service";
import UserGroupsUserSelect from "./user-groups-user-select";
import {useUserService} from "../../services/user-service";
import {useRoleService} from "../../services/role-service";
import {useAuth} from "../../context/auth";
import {useAuthService} from "../../services/auth-service";

const AddEditUserGroupModal = forwardRef( ({onSuccess=null, ...props}, ref) => {
  const [show, setShow] = useState(false);
  const [model, setModel] = useState();
  const [loading, setLoading] = useState(false);
  const [syncData, setSyncData] = useState(userUserGroupRoleRequest.cast());
  const [rolesList, setRolesList] = useState([]);
  const [usersList, setUsersList] = useState([]);
  const [groupUserRows, setGroupUserRows] = useState([]);
  const [currentUserAffected, setCurrentUserAffected] = useState(false);
  const {addErrorMessage, removeMessage } = useGlobalNotificationMessage();
  const [reset, setReset] = useState(0);

  let formSchema = userGroupRequestSchema;

  let userService = useUserService();
  let userGroupService = useUserGroupService();
  let roleService = useRoleService();
  let auth = useAuth();
  let authService = useAuthService();

  //expose api for interacting with component
  useImperativeHandle(ref, () => ({
    openModal: openModal,
    closeModal: closeModal,
  }));

  const openModal = (model=null) => {
    setLoading(true);
    if (model && model.id){

      let promises = [
        reloadRoles(),
        reloadUsers(),
        getDetail(model.id)
      ];

      Promise.all(promises).then(([roles, users, detail=null])=>{
        if (roles){
          setRolesList(roles);
        }
        if (users){
          setUsersList(users);
        }
        if (detail){
          setModel(formSchema.cast(detail));
          setGroupUserRows(!detail.users ? [] : detail.users.map((gu)=>{
            return {
              key: gu.pivot?.id ? gu.pivot.id : HelperService.generateKey(),
              data: gu.pivot
            }
          }));
          detail.users.map((gu)=>{
            if (gu.pivot.user_id === auth.currentUser.id){
              setCurrentUserAffected(true);
            }
          });

        } else {
          setCurrentUserAffected(false);
          setModel(formSchema.cast());
          setGroupUserRows([]);
        }
      }).catch((error)=>{
        HelperService.handleApiErrors(error, addErrorMessage, "Nepodarilo sa získať detail.");
      }).finally(()=>{
        setLoading(false);
      });

    } else {
      setModel(formSchema.cast());
      setGroupUserRows([]);
      setLoading(false);
    }
    setShow(true);
  }
  const closeModal = () => {
      setShow(false);
      setGroupUserRows([]);
      setReset(reset+1);
      setModel(formSchema.cast());
  }

  const getDetail = (id) => {
    return userGroupService.getDetail(id);
  }

  const reloadRoles = () => {
    return roleService.getList();
  }

  const reloadUsers = () => {
    return userService.getList();
  }

  const addGroupUserRow = (row=null) => {
    const newRow = row ? row : {
      key: HelperService.generateKey(),
      data: null
    };
    setGroupUserRows((prevState)=>{
      return [...prevState, newRow];
    })
  }

  const delGroupUserRow = (row) => {
    setGroupUserRows((prevState)=>{
      return [...prevState.filter((o)=>o.key!==row.key)];
    })
  }

  const onUserGroupUsersSelectChange = (key, ugr={}) => {
    setGroupUserRows((prevState)=>{
      return HelperService.addOrReplaceItemInList(prevState, {key: key, data: ugr}, 'key');
    });
  }

  const onSubmit = (values, { setSubmitting, setErrors }) => {
    removeMessage();
    setSubmitting(true);
    const reqValues = values;

    if (groupUserRows){
      let groupuservalid = true;
      reqValues.sync_user_ids = groupUserRows.map((gur)=>{
        if (!gur?.data || !gur.data?.role_id || !gur.data?.user_id){
          groupuservalid = false;
        }
        return gur.data
      });
      if (!groupuservalid){
        setSubmitting(false);
        addErrorMessage("Používateľ aj skupinová rola sú povinné.");
        return false;
      }
    }

    userGroupService.addEditModel(reqValues, reqValues.id)
      .then(async (res) => {
        let currentUserAffectedAfter = HelperService.getObjByKey(res.users, 'id', auth.currentUser.id);
        if (currentUserAffected || currentUserAffectedAfter){
          let user = await authService.getUser();
          auth.doAppUserLogin(user);
        }
        if (onSuccess && 'function' === typeof onSuccess){
          onSuccess(res);
        }
        closeModal();
      })
      .catch(error => {
        HelperService.handleApiErrors(error, addErrorMessage, "Nepodarilo sa uložiť zmeny.");
        HelperService.handleFormErrors(error, setErrors);
      })
      .finally(()=>{ setSubmitting(false);});
  };

  return (
    <>
      <Formik
        key={reset}
        enableReinitialize={true}
        initialValues={formSchema.cast(model) || formSchema.cast()}
        validationSchema={formSchema}
        onSubmit={onSubmit}
      >
        {({ isSubmitting, errors, touched, handleSubmit, setFieldValue}) => (

          <Modal size={model && model.id ? 'lg' : 'sm' } show={!!show} onHide={closeModal} centered>
            <Modal.Header closeButton>
              { model && <Modal.Title>{ !model.id ? 'Nová skupina používateľov' : 'Úprava skupiny používatelov' }</Modal.Title>}
            </Modal.Header>
            <Modal.Body>
              <Section animation={HelperService.getRandomAnimation()}>

                {loading && <Loading className="m-2" />}

                {!loading && <Form className="row">
                  <FormGroup
                    name="title"
                    placeholder="Popisný názov skupiny"
                    label="Názov"
                    className="col-md-12"
                    touched={touched} errors={errors} schema={formSchema}
                  />

                  {model && model.id && <>
                    <div className="col">
                      <h5>Používatelia v skupine</h5>
                      <p className='text-muted'>Priradte užívateľa do skupiny ako správcu, ktorý má na starosti celú skupinu, alebo iba ako bežný účet.</p>
                      {groupUserRows && groupUserRows.map((gur)=>{
                        return <React.Fragment key={'rf_'+gur.key}>
                          <div className="row align-items-center mb-1">
                            <div className="col">
                              <UserGroupsUserSelect
                                key={gur.key}
                                userGroup={model}
                                groupRolesList={rolesList}
                                usersList={usersList}
                                selectedUserGroup={gur.data}
                                onChange={(gs)=>onUserGroupUsersSelectChange(gur.key, gs)}
                              />
                            </div>
                            <div className="col-1">
                              <span className="hover-text-danger clickable" onClick={()=>delGroupUserRow(gur)}><span className="mdi mdi-close-box"></span></span>
                            </div>
                          </div>
                        </React.Fragment>
                      })}
                      <div className="text-center mt-2 mb-1">
                        <span className="hover-text-primary clickable mt-2" onClick={()=>addGroupUserRow()}><span className="mdi mdi-plus"></span> Pridať používateľa do skupiny</span>
                      </div>
                    </div>
                  </>}

                  <div className="clearfix"></div>

                </Form>}

              </Section>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="outline-secondary" onClick={closeModal}>Zatvoriť</Button>
              <Button type="submit" variant="primary" onClick={handleSubmit} disabled={isSubmitting}>Odoslať</Button>
            </Modal.Footer>
          </Modal>
        )}
      </Formik>

    </>
  );
});

export default AddEditUserGroupModal;
